import { isObject, safeGet } from './object.utils';

/**
 * User-facing messages for network-class failures. Kept in one place so the
 * whole monorepo speaks with one voice when the browser/server can't be
 * reached — consumers needing localisation should detect via
 * `isNetworkError()` and substitute their own translation key.
 */
export const NETWORK_ERROR_MESSAGES = {
  offline: 'You appear to be offline. Please check your internet connection.',
  unreachable: 'Could not reach the server. Please check your connection and try again.',
} as const;

/**
 * Detect a transport-level failure (request never completed): Angular's
 * `HttpErrorResponse` sets `status: 0` whenever the response object was
 * never received — DNS failure, dropped connection, CORS pre-flight reject,
 * etc. `statusText` is then `'Unknown Error'`. Some wrappers nest the error
 * one level deep (NGXS error actions), so we probe both levels.
 */
export const isNetworkError = (err: unknown): boolean => {
  if (!isObject(err)) return false;
  const status = safeGet<unknown, null | number>(err, ['status'], null);
  if (status === 0) return true;
  const innerStatus = safeGet<unknown, null | number>(err, ['error', 'status'], null);
  return innerStatus === 0;
};

/**
 * Extract a human-readable message from an arbitrary API / HTTP error payload.
 *
 * Walks the common ASP.NET / FluentValidation / ProblemDetails wire shapes —
 * `error.errors`, `error.error.errors`, `payload.errors`, plus the
 * single-message slots `detail`, `title`, `message` — and returns the first
 * non-empty match. Use it whenever an HTTP-error callback needs to surface a
 * notification: keeps every consumer site at one line and centralises the
 * shape negotiation.
 *
 * Lives in `libs/core` so both apps AND libs (e.g. `NotificationService`)
 * can use the same implementation — single source of truth for error
 * extraction across the platform.
 *
 * @example
 *   notificationService.showError(
 *     extractApiErrorMessage(error) ?? 'Default fallback',
 *   );
 */
export const extractApiErrorMessage = (err: unknown): null | string => {
  if (!isObject(err)) return null;

  // Transport-level failures are surfaced ONCE by `networkErrorInterceptor`.
  // Returning null here means component-level callers (Helper.showError,
  // showErrorOrMessage, etc.) won't double-toast the same friendly message
  // straight after the interceptor — flickering "Connection lost" -> "Connection
  // lost" snackbars. Callers that need their own network handling should
  // check `isNetworkError(err)` explicitly.
  if (isNetworkError(err)) return null;

  // Per-field validation map — try every known wrapper depth. NGXS error
  // actions nest the original HttpErrorResponse at `.error`, and Angular's
  // HttpErrorResponse then nests the parsed response body at `.error`, so
  // ProblemDetails payloads typically land at `actionError.error.error.errors`.
  // We walk shallowest-to-deepest so the FIRST match wins; if all are missing
  // we fall through to the single-message candidates below.
  const errorsMap =
    safeGet<unknown, null | Record<string, unknown>>(err, ['error', 'error', 'errors'], null) ??
    safeGet<unknown, null | Record<string, unknown>>(err, ['error', 'errors'], null) ??
    safeGet<unknown, null | Record<string, unknown>>(err, ['payload', 'error', 'errors'], null) ??
    safeGet<unknown, null | Record<string, unknown>>(err, ['payload', 'errors'], null) ??
    safeGet<unknown, null | Record<string, unknown>>(err, ['errors'], null);

  if (isObject(errorsMap)) {
    const flat = Object.values(errorsMap)
      .flat()
      .map((v): string => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join('\n');
    if (flat) return flat;
  }

  // Single-message slots — ordered deepest-first so we hit the genuine
  // ProblemDetails payload (`error.error.detail`) before Angular's generic
  // `HttpErrorResponse.message` (which reads "Http failure response for ...").
  // `message` paths sit at the very end so they only win when nothing better
  // is on offer.
  const candidates: Array<Array<number | string>> = [
    ['error', 'error', 'detail'],
    ['error', 'error', 'title'],
    ['error', 'error', 'message'],
    ['payload', 'error', 'detail'],
    ['payload', 'error', 'title'],
    ['payload', 'error', 'message'],
    ['error', 'detail'],
    ['error', 'title'],
    ['payload', 'detail'],
    ['detail'],
    ['title'],
    ['error', 'message'],
    ['payload', 'message'],
    ['message'],
  ];
  for (const path of candidates) {
    const value = safeGet<unknown, null | string>(err, path, null);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
    }
  }

  return null;
};
